import React, {useState, useEffect, useRef} from 'react';
import {Box, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography} from '@mui/material';
import {CustomButton, Select} from '..';
import {useFormInput} from '../../utils/utilities';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import {addAdmin, editAdmin, editProfile} from '../../actions';
import {fieldError, isFieldIvalid} from '../../utils/errorHandler';
import { Button } from 'rsuite';
import SignaturePad from 'react-signature-canvas';


const formatArray = array => {
  return array.map(obj => ({id: obj.id, name: obj.name}));
};

const AdminModal = ({adminToEdit, profile}) => {
  const {allBuildings} = useSelector(s => s.buildings);
  const {user, authLoading, authError} = useSelector(s => s.user);
  const {adminsLoading, adminsError} = useSelector(s => s.admins);
  const firstName = useFormInput(
    profile ? user?.firstName : adminToEdit ? adminToEdit.firstName : '',
  );
  const lastName = useFormInput(
    profile ? user.lastName : adminToEdit ? adminToEdit.lastName : '',
  );
  const email = useFormInput(
    profile ? user.email : adminToEdit ? adminToEdit.email : '',
  );
  const phoneNumber = useFormInput(
    profile ? user.phoneNumber : adminToEdit ? adminToEdit.phoneNumber : '',
  );
  const address = useFormInput(
    profile ? user.address : adminToEdit ? adminToEdit.address : '',
  );
  const [formattedBuildings, setFormattedBuildings] = useState([]);
  const birthday = useFormInput(
    profile
      ? moment(profile.birthday).format('yyyy-MM-DD')
      : adminToEdit
      ? moment(adminToEdit.birthday).format('yyyy-MM-DD')
      : moment('1990-01-01').format('yyyy-MM-DD'),
  );
  const [complexes, setComplexes] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    setFormattedBuildings(formatArray(allBuildings));
  }, []);

  useEffect(() => {
    if (formattedBuildings.length > 0 && adminToEdit && adminToEdit.buildings) {
      const values = adminToEdit.buildings.map(obj => obj.id);
      setComplexes(typeof values === 'string' ? values.split(',') : values);
    }
  }, [formattedBuildings]);

  useEffect(() => {
    if (adminToEdit) {
      firstName.manuallyChange(adminToEdit.firstName);
      lastName.manuallyChange(adminToEdit.lastName);
      email.manuallyChange(adminToEdit.email);
      phoneNumber.manuallyChange(adminToEdit.phoneNumber);
      address.manuallyChange(adminToEdit.address);
      birthday.manuallyChange(
        moment(adminToEdit.birthday).format('yyyy-MM-DD'),
      );
    }
  }, [adminToEdit]);

  const onComplexesChange = value => {
    setComplexes(value);
  };

  const onSubmit = e => {
    e.preventDefault();
    const birthdayMs = moment(birthday.value).add(1, 'day').valueOf();
    const values = [
      email.value,
      firstName.value,
      lastName.value,
      phoneNumber.value,
      address.value,
      birthdayMs,
      signatureUrl,
    ];
    if (adminToEdit) {
      dispatch(editAdmin(...values, complexes, adminToEdit.id));
    } else if (profile) {
      values.shift();
      dispatch(editProfile(...values));
    } else {
      dispatch(addAdmin(...values, complexes));
    }
  };

  const [open, setOpen] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState(profile ? (user.signatureUrl ? user.signatureUrl : "") : '');
  const signaturePadRef = useRef({});

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = () => {
    if (signaturePadRef.current) {
      // Access the signature pad instance and get the trimmed canvas
      const trimmedCanvas = signaturePadRef.current.getCanvas();
      const dataURL = trimmedCanvas.toDataURL('image/png');
      setSignatureUrl(dataURL);

      // Store the signature in localStorage
      localStorage.setItem('signatureUrl', dataURL);
    }
    handleClose();
  };

  useEffect(() => {
    const storedSignatureUrl = localStorage.getItem('signatureUrl');
    if (storedSignatureUrl) {
      setSignatureUrl(storedSignatureUrl);
    }
  }, []);

  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  return (
    <>
      <Typography alignSelf="center" variant="h6" mt="1rem" fontWeight="bold">
        {!adminToEdit && !profile && 'Add a new admin'}
      </Typography>
      <form onSubmit={onSubmit}>
        <Box display="flex" justifyContent="center">
          <Box
            display="flex"
            flexDirection="column"
            mt="2rem"
            gap="1rem"
            sx={{
              width: {
                xs: 'calc(100vw - 4rem)',
                sm: adminToEdit ? '70%' : '30rem',
              },
            }}>
            <Box display="flex" gap="1rem">
              <TextField
                {...firstName}
                error={isFieldIvalid(
                  profile ? authError : adminsError,
                  'firstName',
                )}
                helperText={fieldError(
                  profile ? authError : adminsError,
                  'firstName',
                )}
                required
                label="First name"
                fullWidth
              />
              <TextField
                {...lastName}
                error={isFieldIvalid(
                  profile ? authError : adminsError,
                  'lastName',
                )}
                helperText={fieldError(
                  profile ? authError : adminsError,
                  'lastName',
                )}
                required
                label="Last name"
                fullWidth
              />
            </Box>
            <TextField
              {...email}
              disabled={profile}
              error={isFieldIvalid(profile ? authError : adminsError, 'email')}
              helperText={fieldError(
                profile ? authError : adminsError,
                'email',
              )}
              required
              label="Email"
            />
            <Box display="flex" gap="1rem">
              <TextField
                {...phoneNumber}
                error={isFieldIvalid(
                  profile ? authError : adminsError,
                  'phoneNumber',
                )}
                helperText={fieldError(
                  profile ? authError : adminsError,
                  'phoneNumber',
                )}
                required
                label="Phone number"
                fullWidth
              />
              <TextField
                type={'date'}
                {...birthday}
                error={isFieldIvalid(
                  profile ? authError : adminsError,
                  'birthday',
                )}
                helperText={fieldError(
                  profile ? authError : adminsError,
                  'birthday',
                )}
                required
                label="Birthday"
                fullWidth
              />
            </Box>
            <TextField
              {...address}
              error={isFieldIvalid(
                profile ? authError : adminsError,
                'address',
              )}
              helperText={fieldError(
                profile ? authError : adminsError,
                'address',
              )}
              required
              label="Address"
            />
            {profile && ( <div className="center-text">
              <h4 style={{paddingBottom: 10}}>Signature pad</h4>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleClickOpen}>
                Open Pad
              </Button>

              {/* Dialog component from MUI */}
              <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Signature Pad</DialogTitle>
                <DialogContent>
                  {/* Signature Pad Component */}
                  <SignaturePad ref={signaturePadRef} />
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleClose} color="secondary">
                    Close
                  </Button>
                  <Button onClick={clearSignature} color="secondary">
                    Clear
                  </Button>
                  <Button onClick={handleSave} color="primary">
                    Save
                  </Button>
                </DialogActions>
              </Dialog>
              <br />
              <br />
              <img
                src={signatureUrl}
                alt="signature"
                style={{
                  display: 'block',
                  margin: '0 auto',
                  border: '1px solid #000',
                  padding: 10,
                  width: '200px',
                  height: '180px',
                  borderRadius: 5,
                }}
              />
            </div>)}
            {!profile && (
              <Select
                value={complexes}
                onChangeValue={onComplexesChange}
                items={formattedBuildings}
                label="Assign projects"
                multiple
                error={isFieldIvalid(adminsError, 'buldingIds')}
                helperText={fieldError(adminsError, 'buldingIds')}
              />
            )}
            <CustomButton loading={adminsLoading || authLoading} type="submit">
              {adminToEdit || profile ? 'Save' : 'Submit'}
            </CustomButton>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default AdminModal;
